<template>
  <div
    ref="root"
    :class="[
      { disabled: props.disabled },
      props.version === 'v2' ? 'v2-select' : 'og-select',
      props.version === 'v2' ? 'v2-text--' + `${props.fontSize}` : ''
    ]"
  >
    <s-popper
      :class="[
        { 'og-select__control': !$slots.trigger },
        { 'og-select_control--placeholder': !$slots.trigger && !modelValue && placeholder },
        props.version === 'v2' ? 'v2-select-' + `${props.size}` : ''
      ]"
      :disabled="props.disabled"
      key="select-control"
      @closed="_onDropdownClosed"
      @opened="_onDropdownOpened"
      :placement="props.placement"
      :popup-class="popupClass"
      tab-index="0"
      v-model="isDropdownOpen"
      :animation-frame="props.showTags && props.multiple"
      ref="floatingRef"
    >
      <template v-if="$slots.trigger">
        <slot name="trigger" :is-dropdown-open="isDropdownOpen" :value="arrayValue"/>
      </template>
      <template v-else>
        <div class="og-select__content" key="select-content-container">
          <template v-if="(props.ajaxAdapter && getAjaxIsLoading()) || !arrayValue.length">
            <div class="og-select__content__placeholder">{{ props.placeholder }}</div>
          </template>

          <template v-else-if="props.multiple">
            <div class="og-select__content__selection">
              <template v-if="$slots.result">
                <slot name="result" :value="arrayValue"/>
              </template>
              <template v-else>
                {{ $t('extra.selected') }}: {{ arrayValue.length }}
              </template>
            </div>
          </template>

          <template v-else>
            <div class="og-select__content__selection">
              <template v-if="$slots.result">
                <slot name="result" :value="arrayValue[0]"/>
              </template>
              <template v-else>
                {{ getDisplayValue(arrayValue[0]) }}
              </template>
            </div>
          </template>
        </div>

        <div class="og-select__buttons">
          <template v-if="props.nullable && hasValue">
            <div class="og-select__buttons__button" @click="clear">
              <cross-icon class="select__cross-icon"/>
            </div>
          </template>

          <div class="og-select__buttons__button">
            <chevron-down class="transition" :class="[{ 'rotate-180': isDropdownOpen, 'rotate-0': !isDropdownOpen }]"/>
          </div>
          <div class="og-select__buttons__button" v-if="props.language">
            <span :class="`fi fi-${getFlagCode(props.language)}`"/>
          </div>
        </div>
      </template>

      <template #popup>
        <div
          :class="[
            props.version === 'v2' ? 'v2-select-dropdown-menu' : props.arrow ? 'dropdown-menu' : 'og-select-dropdown__container',
            props.version === 'v2' ? 'v2-text--' + `${props.fontSize}` : ''
          ]"
        >
          <template v-if="$slots.title">
            <slot name="title"/>
          </template>
          <template v-if="props.searchable">
            <div class="select-search-wrapper">
              <div class="form-group-feedback-left position-relative">
                <input
                  class="v2-select-search"
                  @click="stopPropagation"
                  @input="_onFilterInput"
                  @keyup.enter="onInputEnter"
                  :placeholder="props.searchPlaceholder"
                  v-focus
                  :value="filter"
                >
                <div class="form-control-feedback form-control-feedback-lg">
                  <search-icon class="icon--small opacity-50" style="top: -2px;"/>
                </div>
              </div>
            </div>
          </template>
          <template v-if="$slots.header">
            <slot name="header" :filter="filter"/>
          </template>
          <div class="og-select-results" v-scroll-end="_onScrollEnd">
            <template v-if="props.showDefault && filter">
              <div @click="selectDefault" class="pt-tiny px-tiny">
                <div class="og-select-result d-flex justify-content-between">
                  <div class="text-break">{{ filter }}</div>
                  <div class="badge px-0">{{ $t('actions.choose') }}</div>
                </div>
              </div>
            </template>
            <template v-for="group in filteredGroups" :key="group.key">
              <div class="og-select-group">
                <template v-if="group.title">
                  <div class="og-select-group__title">{{ group.title }}</div>
                </template>
                <template v-for="(item, index) in group.items" :key="index">
                  <selectable-item
                    :state-fn="props.itemState"
                    :is-selected-fn="isSelected"
                    :item="item"
                    @select="toggleItemSelection"
                  >
                    <template v-if="$slots.item">
                      <slot name="item" :value="item" :is-selected="isSelected(item)"/>
                    </template>
                    <template v-else>
                      {{ getDisplayValue(item) }}
                    </template>
                  </selectable-item>
                </template>
              </div>
            </template>

            <template v-if="isAjaxLoadingList">
              <div class="og-select-loader">
                <div/>
                <div/>
                <div/>
                <div/>
              </div>
            </template>

            <template v-if="!isAjaxLoadingList && !filteredGroups.length">
              <template v-if="$slots.empty">
                <slot name="empty"/>
              </template>
              <template v-else>
                <div class="p-2 text-center">{{ props.emptyText }}</div>
              </template>
            </template>
          </div>
          <template v-if="$slots.footer">
            <div class="select-dropdown-footer">
              <slot name="footer"/>
            </div>
          </template>
        </div>
      </template>
    </s-popper>

    <template v-if="displayTags">
      <div class="og-select-tags" key="select-tags">
        <template v-for="(item, index) in arrayValue" :key="item.key">
          <div class="og-select-tag">
            <template v-if="$slots.tag">
              <slot name="tag" :value="item" :remove="deselectItem" :index="index"/>
            </template>
            <template v-else>
              <tag-label :uppercase="false" closable @remove="deselectItem(item)" :triangle-border="false">
                {{ getDisplayValue(item) }}
              </tag-label>
            </template>
          </div>
        </template>
        <template v-if="$slots['tags-end']">
          <div class="og-select-tag">
            <slot name="tags-end"/>
          </div>
        </template>
      </div>
    </template>

    <template v-if="props.ajaxAdapter && getAjaxIsLoading()">
      <div class="og-select-value-loader">
        <i class="icon-spinner9 spinner mr-2"/>
      </div>
    </template>
  </div>
</template>

<script setup>
  import { focus as vFocus, scrollEnd as vScrollEnd } from '@/directives';
  import { getSize, makeIterator } from '@/utils/data';
  import Fuse from 'fuse.js';
  import debounce from 'lodash/debounce';
  import { toRaw, ref, computed, nextTick, watch, onBeforeUnmount } from 'vue';
  import SelectableItem from '@/components/select/select-item.vue';
  import { langService } from '@/core';
  import TagLabel from '@/pages/partials/tag/tag-label.vue';
  import { getFlagCode } from '@/utils/language';
  import SearchIcon from '@/components/icons-v2/search-icon.vue';
  import ChevronDown from '@/components/icons-v2/chevron-down-icon.vue';
  import CrossIcon from '@/components/icons-v2/cross-icon.vue';

  const VALUE_KEY = '$key';
  const VALUE_KEY_FIELD = '$keyField';
  const VALUE_VALUE = '$value';

  const emit = defineEmits(['selectDefault', 'update:modelValue', 'update:items', 'select', 'keyupEnter', 'closed', 'opened']);

  const props = defineProps({
    modelValue: {
      type: null,
      default: null,
    },
    items: {
      type: [Array, Object],
      default: () => [],
    },
    placeholder: {
      type: String,
      default() {
        return langService.trans('extra.select_item');
      },
    },
    keyField: {
      type: String,
      default: '$value',
    },
    textField: {
      type: String,
      default: '$value',
    },
    matchBy: {
      type: String,
      default: '$keyField',
    },
    missingText: {
      type: String,
      default() {
        return langService.trans('extra.invalid_value');
      },
    },
    emptyText: {
      type: String,
      default() {
        return langService.trans('extra.empty_list');
      },
    },
    disabled: {
      type: Boolean,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    nullable: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    searchKeys: {
      type: Array,
      default: () => [],
    },
    searchPlaceholder: {
      type: String,
      default: null,
    },
    ajaxAdapter: {
      type: Object,
      default: null,
    },
    showTags: {
      type: Boolean,
      default: true,
    },
    dropdownWidth: {
      type: [Number, Function],
      default: null,
    },
    emptyItem: {
      type: null,
      default: null,
    },
    placement: {
      type: String,
      default: 'bottom-start',
    },
    arrow: {
      type: Boolean,
      default: false,
    },
    showDefault: {
      type: Boolean,
      default: false,
    },
    itemState: {
      type: Function,
      default() {
        return {
          disabled: false,
        };
      },
    },
    popupClass: {
      type: String,
      default: 'og-select-dropdown',
    },
    language: {
      type: Object,
      default: null,
    },
    version: {
      type: String,
      default: 'v1',
    },
    size: {
      type: String,
      default: 'base',
    },
    fontSize : {
      type: String,
      default: 'base',
    },
  });

  const isDropdownOpen = ref(false);
  const internalValue = ref(null);
  const filter = ref('');
  const floatingRef = ref(null);
  const root = ref(null);

  const matchByField = computed(() => {
    if (props.matchBy === VALUE_KEY_FIELD) {
      return props.keyField;
    }

    return props.matchBy;
  });

  const matchDirectField = computed(() => matchByField.value !== VALUE_KEY && matchByField.value !== VALUE_VALUE);

  const groups = computed(() => {
    const entries = [];

    if (props.emptyItem) {
      entries.push({
        key: 'empty-item-group',
        title: null,
        items: _wrapItems([props.emptyItem]),
        visible: true,
        ajax: false,
      });
    }

    if (getSize(props.items) > 0) {
      entries.push({
        key: 'item-group',
        title: null,
        items: _wrapItems(props.items),
        visible: props.ajaxAdapter === null,
        ajax: false,
      });
    }

    if (getAjaxSourceItems() && getSize(getAjaxSourceItems()) > 0) {
      entries.push({
        key: 'ajax-group-source',
        title: null,
        items: _wrapItems(getAjaxSourceItems(), true),
        visible: false,
        ajax: false,
      });
    }

    if (getAjaxListItems() && getSize(getAjaxListItems()) > 0) {
      entries.push({
        key: 'ajax-group',
        title: null,
        items: _wrapItems(getAjaxListItems(), true),
        visible: true,
        ajax: true,
      });
    }

    return entries;
  });

  const filteredGroups = computed(() => {
    if (props.ajaxAdapter) {
      return groups.value.filter(group => group.visible && group.items.length);
    }

    if (!filter.value.trim()) {
      return groups.value.filter(group => group.visible && group.items.length);
    }

    const filterGroups = [];
    // eslint-disable-next-line no-useless-assignment
    let _filtered = null;

    for (const group of groups.value) {
      if (!group.visible) {
        continue;
      }

      const fuseKeys = ['text', ...props.searchKeys];

      const fuse = new Fuse(group.items, {
        keys: fuseKeys,
        threshold: 0.0,
        ignoreLocation: true,
        getFn(obj, path) {
          const searchKeys = Array.isArray(path) ? path : [path];

          for (const key of searchKeys) {
            const val = obj[key];

            if (typeof val === 'string') {
              return val;
            }
          }

          return '';
        },
      });
      _filtered = fuse.search(filter.value || '').map(result => result.item);

      if (_filtered.length) {
        filterGroups.push(Object.assign({}, group, { items: _filtered }));
      }
    }

    return filterGroups;
  });

  const hasValue = computed(() => {
    if (props.multiple) {
      return props.modelValue && props.modelValue.length > 0;
    }

    return Boolean(props.modelValue);
  });

  const isAjaxLoadingList = computed(() => {
    if (!props.ajaxAdapter) {
      return false;
    }

    return getAjaxIsLoadingList();
  });

  const arrayValue = computed(() => {
    let valueArray = [];
    if (internalValue.value !== null) {
      valueArray = props.multiple ? internalValue.value : [internalValue.value];
    }

    return valueArray;
  });

  const displayTags = computed(() => {
    if (props.ajaxAdapter && getAjaxIsLoading()) {
      return false;
    }

    return props.showTags && props.multiple && arrayValue.value.length > 0;
  });

  const removeValueUpdateWatcher = watch(() => [props.modelValue, props.items, getAjaxSourceItems(), props.textField, props.keyField].filter(x => Boolean(x)), () => {
    _updateInternalValue(props.modelValue);
  }, { immediate: true });

  onBeforeUnmount(() => {
    removeValueUpdateWatcher();
  });

  function getDisplayValue(internalItem) {
    if (typeof internalItem.text === 'object') {
      return JSON.stringify(internalItem.text);
    }

    return internalItem.text;
  }

  function stopPropagation(event) {
    event.stopPropagation();
  }

  function toggleItemSelection(wrappedItem) {
    if (isSelected(wrappedItem)) {
      deselectItem(wrappedItem);
    } else {
      selectItem(wrappedItem);
    }

    if (props.multiple) {
      updateDropdownWidth();
    }
  }

  function selectItem(wrappedItem) {
    if (wrappedItem.missing) {
      return;
    }

    if (wrappedItem.ajax) {
      addItemToList(wrappedItem);
    }

    if (props.multiple) {
      const value = Array.isArray(internalValue.value) ? internalValue.value : [];

      if (props.keyField === VALUE_VALUE && matchDirectField.value) {
        emit('update:modelValue', value.map(selection => selection.item).concat([wrappedItem.item]));
      } else {
        emit('update:modelValue', value.map(selection => selection.key).concat([wrappedItem.key]));
      }
    } else {
      if (props.keyField === VALUE_VALUE && matchDirectField.value) {
        emit('update:modelValue', wrappedItem.item);
      } else {
        emit('update:modelValue', wrappedItem.key);
      }
    }

    if (isDropdownOpen.value && !props.multiple) {
      isDropdownOpen.value = false;
    }
  }

  function deselectItem(wrappedItem) {
    if (!props.multiple) {
      isDropdownOpen.value = false;
      return;
    }

    const value = toRaw(Array.isArray(internalValue.value) ? internalValue.value : []);
    if (props.keyField === VALUE_VALUE && matchDirectField.value) {
      emit('update:modelValue', value
        .filter(item => item.key !== wrappedItem.key)
        .map(selection => {
          if (selection.item) {
            return selection.item;
          }
          return { [matchByField.value]: selection.key };
        }),
      );
    } else {
      emit('update:modelValue', internalValue.value
        .filter(item => item.key !== wrappedItem.key)
        .map(item => item.key),
      );
    }
  }

  function isSelected(wrappedItem) {
    if (internalValue.value === null) {
      return false;
    }

    if (props.multiple) {
      return Boolean(internalValue.value.find(item => item.key === wrappedItem.key));
    }

    return internalValue.value && internalValue.value.key === wrappedItem.key;
  }

  function clear(event = null) {
    if (event) {
      event.stopPropagation();
    }

    emit('update:modelValue', null);

    if (isDropdownOpen.value) {
      isDropdownOpen.value = false;
    }
  }

  function addItemToList(wrappedItem) {
    if (props.keyField === VALUE_KEY && !matchDirectField.value) {
      emit('update:items', Object.assign({}, props.items, { [wrappedItem.key]: wrappedItem.item }));
      return;
    }

    if (props.keyField === VALUE_VALUE && !matchDirectField.value) {
      if (props.items.indexOf(wrappedItem.key) >= 0) {
        return;
      }

      emit('update:items', props.items.concat([wrappedItem.key]));
      return;
    }

    const matches = _matchSelectedItems([wrappedItem.key]).filter(match => !match.missing);
    if (matches.length) {
      return;
    }

    emit('update:items', props.items.concat([wrappedItem.item]));
    props.ajaxAdapter?.addItem(wrappedItem.item);
  }

  function setFilterValue(value) {
    filter.value = value;

    if (isDropdownOpen.value) {
      _triggerFilter();
    }
  }

  function closeDropdown() {
    isDropdownOpen.value = false;
  }

  function _getItemKey(item, index) {
    if (matchDirectField.value && typeof item !== 'object') {
      throw new Error(`Unsupported key field "${matchByField.value}" with simple array items.`);
    }

    switch (matchByField.value) {
      case VALUE_VALUE:
        return item;
      case VALUE_KEY:
        return index;
      default:
        return item[matchByField.value];
    }
  }

  function _getItemText(item, index) {
    if (props.textField !== VALUE_VALUE && props.textField !== VALUE_KEY && typeof item !== 'object') {
      throw new Error(`Unsupported text field "${props.textField}" with simple array items.`);
    }

    switch (props.textField) {
      case VALUE_VALUE:
        return item;
      case VALUE_KEY:
        return index;
      default:
        return item[props.textField];
    }
  }

  function _wrapItems(items, ajax = false) {
    const list = [];

    for (const { key, value } of makeIterator(items)) {
      list.push({
        key: _getItemKey(value, key),
        text: _getItemText(value, key),
        item: value,
        missing: false,
        ajax: ajax,
      });
    }

    return list;
  }

  function _updateInternalValue(value) {
    if ((!props.emptyItem && value === null) || value === undefined) {
      internalValue.value = null;
      props.ajaxAdapter?.updateValue(null);
      return;
    }

    const normalizedValue = Array.isArray(value) ? value : [value];
    const matches = _matchSelectedItems(normalizedValue);

    if (props.emptyItem) {
      if (matches.length && matches[0].missing) {
        internalValue.value = groups.value[0].items[0];
      }
    }

    internalValue.value = props.multiple ? matches : matches[0];
    props.ajaxAdapter?.updateValue(internalValue.value);
  }

  function _matchSelectedItems(values) {
    const list = groups.value.filter(group => !group.ajax);
    const groupLength = list.length;
    const matchMap = new Map();
    // eslint-disable-next-line no-useless-assignment
    let _keyIndex = -1;
    let _matched = false;
    // eslint-disable-next-line no-useless-assignment
    let _itemLength = 0;
    // eslint-disable-next-line no-useless-assignment
    let _group = null;
    // eslint-disable-next-line no-useless-assignment
    let _item = null;

    const keyList = values.map(key => {
      if (key && typeof key === 'object' && matchDirectField.value) {
        matchMap.set(key[matchByField.value], null);
        return key[matchByField.value];
      }

      matchMap.set(key, null);
      return key;
    });

    for (let i = 0; i < groupLength; i++) {
      _group = list[i];
      _itemLength = _group.items.length;

      for (let j = 0; j < _itemLength; j++) {
        _item = _group.items[j];
        _keyIndex = keyList.indexOf(_item.key);

        if (_keyIndex >= 0) {
          _matched = true;
          matchMap.set(keyList[_keyIndex], _item);
        }

        if (!props.multiple && _matched) {
          return [matchMap.values().next().value];
        }
      }
    }

    const result = [];
    matchMap.forEach(((value, key) => {
      if (value === null) {
        result.push({
          key: key,
          text: props.missingText,
          item: null,
          missing: true,
          ajax: false,
        });
      } else {
        result.push(value);
      }
    }));

    return result;
  }

  function _onDropdownOpened() {
    updateDropdownWidth();

    if (props.ajaxAdapter) {
      props.ajaxAdapter.loadNextPage();
    }

    isDropdownOpen.value = true;
    emit('opened');
  }

  async function updateDropdownWidth() {
    await nextTick(() => {
      if (floatingRef.value?.floating) {
        if (typeof props.dropdownWidth === 'function') {
          floatingRef.value.floating.style.width = `${props.dropdownWidth()}px`;

          return;
        }

        if (props.dropdownWidth) {
          floatingRef.value.floating.style.width = `${props.dropdownWidth}px`;
        } else {
          floatingRef.value.floating.style.width = `${root.value.clientWidth}px`;
        }
      }
    });
  }

  function _onDropdownClosed() {
    isDropdownOpen.value = false;
    setFilterValue('');
    emit('closed');
  }

  function _onScrollEnd() {
    props.ajaxAdapter?.loadNextPage();
  }

  function _onFilterInput(e) {
    setFilterValue(e.target.value);
  }

  const _triggerFilter = debounce(() => {
    props.ajaxAdapter?.search(filter.value);
  }, 150);

  function selectDefault() {
    emit('selectDefault', filter.value);
  }

  function onInputEnter() {
    if (filter.value) {
      emit('keyupEnter', filter.value);
      isDropdownOpen.value = false;
    }
  }

  function getAjaxIsLoading() {
    if (typeof props.ajaxAdapter?.isLoadingValue === 'function') {
      return props.ajaxAdapter.isLoadingValue();
    }

    return props.ajaxAdapter?.isLoadingValue;
  }

  function getAjaxSourceItems() {
    if (typeof props.ajaxAdapter?.sourceItems === 'function') {
      return props.ajaxAdapter.sourceItems();
    }

    return props.ajaxAdapter?.sourceItems;
  }

  function getAjaxListItems() {
    if (typeof props.ajaxAdapter?.listItems === 'function') {
      return props.ajaxAdapter.listItems();
    }

    return props.ajaxAdapter?.listItems;
  }

  function getAjaxIsLoadingList() {
    if (typeof props.ajaxAdapter?.isLoadingList === 'function') {
      return props.ajaxAdapter.isLoadingList();
    }

    return props.ajaxAdapter?.isLoadingList;
  }
  defineExpose({ setFilterValue, closeDropdown, isDropdownOpen, filter, _updateInternalValue });
</script>
